import React, { useEffect, useRef } from "react";
import "./ShopGame.css";
import { CUSTOMERS, MAX_STARS, ShopResult } from "./shopGame.ts";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface ShopGameSummaryProps {
    result: ShopResult;
    /** Best score at this level and pace on this device, after this shift. */
    best: number | null;
    isNewBest: boolean;
    /** Where the practice behind this level lives, by slug; looked up in the manifest. */
    practiceSlugs: string[];
    onPlayAgain(): void;
}

/**
 * The end of a shift (ADR 0131 §1, §5): stars out of 20, the orders served, the wrong
 * tries and walk-outs, the best at this level and pace, and the worksheets that practise
 * what the level was about. Takes the focus when it appears, so a keyboard or screen
 * reader user lands on it rather than on a counter that has just closed.
 */
export function ShopGameSummary({ result, best, isNewBest, practiceSlugs, onPlayAgain }: ShopGameSummaryProps) {
    const t = useTranslation();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        ref.current?.focus({ preventScroll: true });
    }, []);

    const links = practiceSlugs.flatMap((slug) => {
        const route = findBySlug(slug);
        return route ? [{ slug: route.slug, text: route.h1 ?? route.slug }] : [];
    });

    return (
        <div className="shopSummary" ref={ref} tabIndex={-1}>
            <p className="shopSummaryHeading">{t("flashCards.shopFinal")}</p>
            <p className="shopSummaryScore">{result.stars}</p>
            <p className="shopSummaryCaption">{format(t("flashCards.shopScoreCaptionTemplate"), { max: MAX_STARS })}</p>
            <p className="shopSummaryCounts">
                {format(t("flashCards.shopSummaryServedTemplate"), { n: result.ordersServed, total: CUSTOMERS })}
            </p>
            <p className="shopSummaryCounts">{format(t("flashCards.shopSummaryWrongTemplate"), { n: result.wrongTries })}</p>
            {result.walkedOut > 0 && (
                <p className="shopSummaryCounts">
                    {format(t("flashCards.shopSummaryWalkedTemplate"), { n: result.walkedOut })}
                </p>
            )}

            {best !== null && (
                <p className={`shopSummaryBest${isNewBest ? " shopSummaryBest--new" : ""}`}>
                    {isNewBest ? t("flashCards.newBest") : format(t("flashCards.yourBestTemplate"), { n: best })}
                </p>
            )}

            <button type="button" className="btn btn-primary shopPlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>

            {links.length > 0 && (
                <div className="shopPractice">
                    <h3>{t("flashCards.shopPracticeHeading")}</h3>
                    <ul>
                        {links.map((link) => (
                            <li key={link.slug}>
                                <a href={link.slug}>{link.text}</a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default ShopGameSummary;
